/**
 * Framework-free pub/sub notifier. A single module-scope emitter
 * is shared between React (`useNotifier()`) and non-React callers
 * (e.g. MSAL event callbacks registered in `main.tsx`).
 *
 * The `ToastHost` component subscribes and renders each toast for
 * a short, level-dependent duration.
 */
export type ToastLevel = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  readonly id: number;
  readonly level: ToastLevel;
  readonly message: string;
  /** Auto-dismiss delay in ms; `0` disables auto-dismiss. */
  readonly durationMs: number;
}

export interface ToastInput {
  readonly level?: ToastLevel;
  readonly message: string;
  readonly durationMs?: number;
}

type Listener = (toast: Toast) => void;

const listeners = new Set<Listener>();
let nextId = 1;

export function subscribeToToasts(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function emitToast(input: ToastInput): Toast {
  const toast: Toast = {
    id: nextId++,
    level: input.level ?? 'info',
    message: input.message,
    durationMs: input.durationMs ?? defaultDuration(input.level ?? 'info'),
  };
  for (const listener of listeners) {
    listener(toast);
  }
  return toast;
}

function defaultDuration(level: ToastLevel): number {
  switch (level) {
    case 'error':
      return 8_000;
    case 'warning':
      return 6_000;
    case 'success':
      return 4_000;
    case 'info':
    default:
      return 4_000;
  }
}
